import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TopbarSearch } from '@/components/ui/TopbarSearch';
import { ActionMenu } from '@/components/ui/ActionMenu';
import { CardList } from '@/components/ui/CardList';
import { useEquipmentRequests } from './hooks/useEquipmentRequests';
import { cn } from '@/utils/cn';
import { 
  Truck, 
  Timer, 
  CheckCircle2, 
  Activity, 
  Plus, 
  Eye, 
  Settings2,
  Calendar,
  AlertCircle
} from 'lucide-react';

export function EquipmentRequestListPage() {
  const navigate = useNavigate();
  const { requests, stats, filters, isLoading, setFilters } = useEquipmentRequests();

  const statusMap = {
    requested: { label: 'Chờ duyệt', color: 'bg-amber-500', text: 'text-amber-600', bg: 'bg-amber-50' },
    approved: { label: 'Đã duyệt', color: 'bg-blue-500', text: 'text-blue-600', bg: 'bg-blue-50' },
    in_use: { label: 'Đang dùng', color: 'bg-emerald-500', text: 'text-emerald-600', bg: 'bg-emerald-50' },
    returned: { label: 'Đã trả', color: 'bg-slate-400', text: 'text-slate-500', bg: 'bg-slate-50' },
    rejected: { label: 'Từ chối', color: 'bg-red-500', text: 'text-red-600', bg: 'bg-red-50' },
  };

  const kpis = [
    { label: 'Tổng yêu cầu', value: stats.total, Icon: Truck, tone: 'text-slate-700' },
    { label: 'Chờ duyệt', value: stats.requested, Icon: Timer, tone: 'text-amber-600' },
    { label: 'Đã duyệt', value: stats.approved, Icon: CheckCircle2, tone: 'text-blue-600' },
    { label: 'Đang dùng', value: stats.inUse, Icon: Activity, tone: 'text-emerald-600' },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-md border border-slate-100 bg-white p-3">
        <div className="space-y-1">
          <h1 className="text-lg font-normal text-slate-900 tracking-tight">Yêu cầu thiết bị</h1>
          <p className="sm:text-sm text-xs text-slate-500">Điều phối và quản lý thiết bị thi công tại các dự án.</p>
        </div>
        <button
          onClick={() => navigate('new')}
          className="flex h-10 items-center justify-center gap-2 rounded-md bg-slate-900 px-4 sm:text-sm text-xs font-normal text-white transition-all hover:bg-slate-800 active:scale-95 -slate-900/10"
        >
          <Plus className="h-4 w-4" />
          <span>Tạo yêu cầu</span>
        </button>
      </div>

      {/* KPI Stats */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {kpis.map(({ label, value, Icon, tone }) => (
          <div key={label} className="rounded-md border border-slate-100 bg-white p-3">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-[10px] font-normal text-slate-400 tracking-widest">{label}</p>
                <p className={cn('text-xl font-normal', tone)}>{value}</p>
              </div>
              <Icon className={cn('h-5 w-5 opacity-20', tone)} />
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="rounded-md border border-slate-100 bg-white p-3">
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <div className="relative flex-1">
            <TopbarSearch
              placeholder="Tìm yêu cầu, dự án, người gửi..."
              value={filters.query}
              onChange={(query) => setFilters({ ...filters, query })}
            />
          </div>
          <div className="flex items-center gap-2 overflow-x-auto pb-1 md:pb-0">
            {(['all', 'requested', 'approved', 'in_use', 'returned'] as const).map((status) => (
              <button
                key={status}
                onClick={() => setFilters({ ...filters, status })}
                className={cn(
                  'whitespace-nowrap rounded-md px-3 py-1.5 sm:text-sm text-xs font-normal transition-all',
                  filters.status === status
                    ? 'bg-slate-900 text-white '
                    : 'bg-slate-50 text-slate-500 border border-transparent hover:bg-slate-100'
                )}
              >
                {status === 'all' ? 'Tất cả' : statusMap[status as keyof typeof statusMap].label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden rounded-md border border-slate-100 bg-white overflow-hidden md:block">
        <table className="w-full text-left sm:text-sm text-xs">
          <thead>
            <tr className="border-b border-slate-50 bg-slate-50/50 sm:text-sm text-xs font-normal text-slate-500">
              <th className="p-3 pl-6 font-normal">Yêu cầu / Mã</th>
              <th className="p-3 font-normal">Dự án thi công</th>
              <th className="p-3 font-normal">Thời gian sử dụng</th>
              <th className="p-3 font-normal">Người yêu cầu</th>
              <th className="p-3 text-center font-normal">Trạng thái</th>
              <th className="p-3 pr-6 text-right font-normal">Thao tác</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="animate-pulse">
                  <td colSpan={6} className="p-3"><div className="h-10 rounded-md bg-slate-50" /></td>
                </tr>
              ))
            ) : requests.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-10 text-center text-slate-400">Không tìm thấy yêu cầu nào</td>
              </tr>
            ) : (
              requests.map((item) => (
                <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="p-3 pl-6">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-600">
                        <Settings2 className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-normal text-slate-800 truncate">{item.title}</p>
                        <p className="text-[10px] font-normal text-slate-400 tracking-tighter">
                          EREQ-{item.id.substring(0, 8)}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 font-normal text-slate-600 sm:text-sm text-xs">{item.project?.name || '-'}</td>
                  <td className="p-3">
                    <div className="flex flex-col text-[10px] font-normal text-slate-500 tracking-tighter">
                       <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(item.neededFrom).toLocaleDateString('vi-VN')}</span>
                       {item.neededUntil && <span className="text-slate-300">đến {new Date(item.neededUntil).toLocaleDateString('vi-VN')}</span>}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="h-6 w-6 rounded-full bg-slate-100" />
                      <span className="sm:text-sm text-xs font-normal text-slate-600">{item.requester?.full_name || '-'}</span>
                    </div>
                  </td>
                  <td className="p-3 text-center">
                    <span className={cn('inline-flex items-center gap-1.5 font-normal text-[10px] px-2 py-0.5 rounded', statusMap[item.statusCode as keyof typeof statusMap].bg, statusMap[item.statusCode as keyof typeof statusMap].text)}>
                      {statusMap[item.statusCode as keyof typeof statusMap].label}
                    </span>
                  </td>
                  <td className="p-3 pr-6 text-right">
                    <ActionMenu
                      options={[
                        { label: 'Chi tiết', icon: 'ri-eye-line', onClick: () => navigate(`detail/${item.id}`) },
                        { label: 'Chỉnh sửa', icon: 'ri-edit-line', onClick: () => navigate(`edit/${item.id}`) },
                        { label: 'Bàn giao', icon: 'ri-truck-line', onClick: () => {} },
                        { label: 'Xóa', icon: 'ri-delete-bin-line', variant: 'danger', onClick: () => {} },
                      ]}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card List */}
      <div className="md:hidden">
        <CardList
          items={requests}
          isLoading={isLoading}
          emptyMessage="Không tìm thấy yêu cầu nào"
          renderCard={(item) => (
            <div key={item.id} className="rounded-md border border-slate-100 bg-white p-3 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-600">
                    <Truck className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-normal text-slate-800 truncate">{item.title}</p>
                    <p className="text-[10px] font-normal text-slate-400">EREQ-{item.id.substring(0, 8)}</p>
                  </div>
                </div>
                <ActionMenu
                  options={[
                    { label: 'Chi tiết', icon: 'ri-eye-line', onClick: () => navigate(`detail/${item.id}`) },
                    { label: 'Chỉnh sửa', icon: 'ri-edit-line', onClick: () => navigate(`edit/${item.id}`) },
                  ]}
                />
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-slate-50">
                <span className="text-[10px] font-normal text-slate-500">{item.project?.name || '-'}</span>
                <span className={cn('px-2 py-0.5 rounded text-[10px] font-normal ', statusMap[item.statusCode as keyof typeof statusMap].bg, statusMap[item.statusCode as keyof typeof statusMap].text)}>
                  {statusMap[item.statusCode as keyof typeof statusMap].label}
                </span>
              </div>
            </div>
          )}
        />
      </div>
    </div>
  );
}
